// src/components/antenna/antenna-perception-overview.tsx
import React from "react";
import { ClickableMetric } from "../clickable-metric";
import { AiIndexGauge } from "./ai-index-gauge";
import { AnimatedNumber } from "./animated-number";
import { MetricTerm } from "../metric-term";
import { TooltipBadge } from "../tooltip-badge";
import { TooltipIcon } from "../tooltip-icon";
import { MetricInsightChips } from "../metric-insight-chips";
import { ModelFilterTabs } from "../model-filter-tabs";
import {
  getModelData,
  getVelocityTrendIcon,
  roundScore
} from "@/app/utils/brand-helpers";
import { metricExplanations, overallScoreInsights } from "../../data/antenna-metric-explanations";
import { formatDateTime } from "@/app/utils/date";
import { normalizeProviderKey, getModelLabel } from "@/app/constants/models";

interface AntennaPerceptionOverviewProps {
  perceptionData: any;
  perceptionDataByModel?: Record<string, any>;
  heroMetrics?: any;
  heroMetricsByModel?: Record<string, any>;
  modelComparison: any[];
  /** Planned lanes that produced no analysis; counted in the planned total. */
  failedModelCount?: number;
  modelTabs: any[];
  selectedFilter: string;
  onFilterChange: (val: string) => void;
  onOpenAIChat: (msg: string) => void;
  baselineSnapshotText: string;
}

export function AntennaPerceptionOverview({
  perceptionData,
  perceptionDataByModel = {},
  heroMetrics = {},
  heroMetricsByModel = {},
  modelComparison,
  failedModelCount = 0,
  modelTabs,
  selectedFilter,
  onFilterChange,
  onOpenAIChat,
  baselineSnapshotText
}: AntennaPerceptionOverviewProps) {

  const currentModelData = getModelData(modelComparison, selectedFilter);
  const isAll = selectedFilter === "all";

  // perceptionDataByModel comes from the backend keyed by its own normalized
  // provider id — re-key it through the frontend's normalizer too so a lookup
  // never silently misses just because the two sides' key formats don't match.
  const normalizedPerceptionByModel = React.useMemo(() => {
    const out: Record<string, any> = {};
    for (const [rawKey, value] of Object.entries(perceptionDataByModel)) {
      out[normalizeProviderKey(rawKey)] = value;
    }
    return out;
  }, [perceptionDataByModel]);

  const currentModelPerception = isAll
    ? null
    : normalizedPerceptionByModel[normalizeProviderKey(selectedFilter)];

  const currentScore = roundScore(isAll ? (perceptionData?.overallScore ?? 0) : (currentModelData?.score ?? 0));

  // Accuracy — same 0-100 scale as the gauge, so band it the same way the
  // "Overall Perception Score" goodRange copy already describes (70-79 Good,
  // 80-89 Excellent, 90+ Outstanding, below 70 needs work), so the color is
  // consistent with what the modal tells people to expect.
  const accuracyDisplay = (isAll ? perceptionData?.accuracy : currentModelPerception?.accuracy) ?? "—";
  const accuracyNum = parseFloat(String(accuracyDisplay).replace("%", ""));
  const accuracyColor = Number.isNaN(accuracyNum)
    ? "var(--foreground)"
    : accuracyNum >= 80
    ? "var(--accent)"
    : accuracyNum >= 70
    ? "var(--signal-blue)"
    : "var(--infrared-coral)";

  // Velocity is a self-reported trend (Rising/Stable/Falling), not a percentage —
  // pull the actual trend word for the per-model view instead of the raw 0-100
  // momentum score that backs the color/sort logic, so it never gets rendered
  // as a meaningless "8%".
  const velocityTrendWord = isAll
    ? String(perceptionData?.velocity ?? "Stable")
    : String(currentModelPerception?.velocity ?? "Stable");

  // Color Logic
  const velocityColor = isAll
    ? (String(perceptionData?.velocity ?? "").toLowerCase() === "rising" ? "var(--accent)" :
       String(perceptionData?.velocity ?? "").toLowerCase() === "falling" ? "var(--infrared-coral)" :
       String(perceptionData?.velocity ?? "").toLowerCase() === "stable" ? "var(--accent)" :
       "var(--muted-foreground)")
    : (currentModelData?.velocityColor ?? "var(--muted-foreground)");

  // Favorability replaces the Tier stat so the index card shows the three
  // inputs that make up the Upmode AI Index: Favorability, Accuracy, Velocity.
  // The number is the sentiment snapshot (% of AI mentions framed positively),
  // the same value the Business Metrics "Favorability" card renders, sourced
  // from heroMetrics.sentimentSnapshot. Re-key the per-model map through the
  // frontend normalizer so a per-model lookup never misses on key-format drift.
  const normalizedHeroByModel = React.useMemo(() => {
    const out: Record<string, any> = {};
    for (const [rawKey, value] of Object.entries(heroMetricsByModel)) {
      out[normalizeProviderKey(rawKey)] = value;
    }
    return out;
  }, [heroMetricsByModel]);

  const activeHeroMetrics = isAll
    ? heroMetrics
    : (normalizedHeroByModel[normalizeProviderKey(selectedFilter)] ?? heroMetrics);
  const favorabilityItem = activeHeroMetrics?.sentimentSnapshot ?? null;
  // Mirrors the old favorability card logic: an empty/"No themes detected" calculation
  // means the backend produced no sentiment for this snapshot, which is "no
  // data" rather than a real 0%, so render a dash like Accuracy does.
  const favorabilityHasData = Boolean(
    favorabilityItem?.calculation &&
    !String(favorabilityItem.calculation).startsWith("No themes detected")
  );
  const favorabilityPct = Number(favorabilityItem?.percentage ?? 0);
  const favorabilityDisplay = favorabilityHasData ? `${favorabilityPct}%` : "—";
  const favorabilityColor = !favorabilityHasData
    ? "var(--foreground)"
    : favorabilityPct >= 60
    ? "var(--accent)"
    : favorabilityPct >= 35
    ? "var(--signal-blue)"
    : "var(--infrared-coral)";

  return (
    <div
      className="relative overflow-hidden rounded-xl p-8 transition-all hover-lift"
      style={{
        background: "linear-gradient(135deg, rgba(126,95,224,0.05), rgba(0,201,196,0.035) 58%, var(--card))",
        border: "1px solid var(--border)",
        borderRadius: "16px",
        boxShadow: "0 1px 2px rgba(13,13,15,.04), 0 12px 32px -16px rgba(13,13,15,.12)",
        overflow: "hidden",
      }}
    >
      <div className="space-y-8">
        <div className="flex items-start justify-between" style={{ gap: 16 }}>
          <div>
            <h3 style={{ fontSize: "var(--text-lg)", fontWeight: "var(--font-weight-semibold)" }}>The Upmode AI Index</h3>
            <p style={{ color: "var(--muted-foreground)", marginTop: "4px", fontSize: "var(--text-sm)" }}>
              The single composite score for how accurately and favorably AI describes your brand, across every model tracked.
            </p>
          </div>
          <div style={{ flexShrink: 0 }}>
            <ModelFilterTabs
              models={modelTabs}
              selectedModel={selectedFilter}
              onModelChange={onFilterChange}
              combinedLabel="All Models"
              includeCombined={true}
            />
          </div>
        </div>

        <div className="@container p-8 rounded-xl" style={{ backgroundColor: "var(--muted)", border: "1px solid var(--border)" }}>
          <div className="flex flex-col @2xl:flex-row items-center gap-12">
            
            {/* GAUGE CHART SECTION */}
            <div className="flex-shrink-0 flex flex-col items-center gap-4">
               <AiIndexGauge
                 score={currentScore}
                 isAll={isAll}
                 velocityColor={velocityColor}
                 modelDisplay={getModelLabel(selectedFilter)}
                 onOpenAIChat={onOpenAIChat}
               />
               
               <div className="text-center">
                  <label style={{ color: "var(--muted-foreground)", fontSize: "var(--text-sm)" }}>Upmode AI Index</label>
               </div>
            </div>

            {/* METRICS SECTION */}
            <div className="@container flex-1 w-full min-w-0">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <h3 style={{ fontSize: "var(--text-lg)", fontWeight: "var(--font-weight-semibold)" }}>
                    <MetricTerm {...metricExplanations["Overall Perception Score"]} onOpenAIChat={() => onOpenAIChat("Overall Perception Score")}>
                      {isAll ? "Upmode AI Index" : `${getModelLabel(selectedFilter)} Score`}
                    </MetricTerm>
                  </h3>
                  <TooltipBadge tooltip={isAll ? "Tier computed for combined view." : `Viewing ${getModelLabel(selectedFilter)} data`} style={{ backgroundColor: isAll ? "var(--success-bg)" : `${velocityColor}15`, color: isAll ? "var(--success-text)" : velocityColor, borderColor: isAll ? "var(--success-border)" : velocityColor, padding: "4px 10px", fontSize: "var(--text-xs)" }}>
                    {isAll ? (perceptionData?.tier ?? "—") : getModelLabel(selectedFilter)}
                  </TooltipBadge>
                </div>
                <p style={{ color: "var(--muted-foreground)", fontSize: "var(--text-sm)" }}>{isAll ? "A blended 0-100 score across every AI model you're tracking" : `How accurately and favorably ${getModelLabel(selectedFilter)} describes your brand`}</p>
              </div>

              <div className="grid grid-cols-1 @md:grid-cols-3 gap-8 mb-4">
                <div className="space-y-1 min-w-0">
                    <label style={{ color: "var(--muted-foreground)", display: "block", fontSize: "var(--text-xs)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Favorability</label>
                    <ClickableMetric value={favorabilityHasData ? <AnimatedNumber value={favorabilityPct} format={(n) => `${Math.round(n)}%`} /> : favorabilityDisplay} label="Favorability" calculation="The share of what AI says about your brand that is framed positively, drawn from the sentiment of every tracked model's response." onClick={() => onOpenAIChat(favorabilityHasData ? `Favorability is ${favorabilityDisplay} positive. How is this measured?` : "How is favorability measured?")} style={{ fontSize: "var(--text-3xl)", fontWeight: 700, color: favorabilityColor, fontVariantNumeric: "tabular-nums" }} hoverColor={favorabilityColor} />
                </div>
                <div className="space-y-1 min-w-0">
                    <label style={{ color: "var(--muted-foreground)", display: "block", fontSize: "var(--text-xs)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Accuracy</label>
                    <ClickableMetric value={Number.isNaN(accuracyNum) ? accuracyDisplay : <AnimatedNumber value={accuracyNum} format={(n) => `${Math.round(n)}${String(accuracyDisplay).includes("%") ? "%" : ""}`} />} label="Accuracy" calculation="How confident the model says it is in the information it has about your brand." onClick={() => onOpenAIChat("Explain my accuracy.")} style={{ fontSize: "var(--text-3xl)", fontWeight: 700, color: accuracyColor, fontVariantNumeric: "tabular-nums" }} hoverColor={accuracyColor} />
                </div>
                <div className="space-y-1 min-w-0">
                    <label style={{ color: "var(--muted-foreground)", display: "block", fontSize: "var(--text-xs)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Velocity</label>
                    <div className="flex items-center gap-2 min-w-0">
                        <ClickableMetric value={velocityTrendWord} label="Velocity" calculation="The model's own read on your brand's momentum right now: trending up, holding steady, or trending down." onClick={() => onOpenAIChat("Velocity trend?")} style={{ fontSize: "var(--text-3xl)", fontWeight: 700, color: velocityColor, fontVariantNumeric: "tabular-nums" }} hoverColor={velocityColor} />
                        <TooltipIcon Icon={getVelocityTrendIcon(velocityTrendWord)} tooltip={`Trending ${velocityTrendWord}`} size={20} style={{ color: velocityColor }} />
                    </div>
                </div>
              </div>

              <MetricInsightChips insights={overallScoreInsights} onOpenAIChat={() => onOpenAIChat("Overall Perception Score insights")} />
              
              <div className="mt-6 pt-4 border-t" style={{ borderColor: "var(--border)" }}>
                <p style={{ color: "var(--muted-foreground)", fontSize: "var(--text-xs)" }}>Analysis: {failedModelCount > 0 ? `${modelComparison.length} of ${modelComparison.length + failedModelCount}` : modelComparison.length} AI models • {formatDateTime(baselineSnapshotText)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}